<script setup lang="ts">
import gsap from 'gsap';
import Lenis from 'lenis';
import { Vector3 } from 'three';

import { type TresContext, TresCanvas } from '@tresjs/core';
import { Levioso, Html } from '@tresjs/cientos';

import { useRenderLoop } from '@tresjs/core';

import { projects } from 'src/data/projects';

import { getTresCanvasSettings } from 'src/composables/getTresCanvasSettings';

const { tier, particleCount, dprCap } = use3DQuality();

const router = useRouter();

let lenisInstance: Lenis | null = null;
let lenisRafId = 0;

const initLenis2 = (el: HTMLElement) => {
  const lenis = new Lenis({
    wrapper: el,
    content: el.firstChild as HTMLElement,
    smoothWheel: true,
    touchMultiplier: 2,
    infinite: true,
    syncTouch: true,
    duration: 1.2,
  });

  function raf(time: number) {
    lenis.raf(time);
    lenisRafId = requestAnimationFrame(raf);
  }

  lenisRafId = requestAnimationFrame(raf);
  lenisInstance = lenis;

  lenis.on('scroll', (e: Lenis) => {
    lenisSrollEvent.value = e;

    const { velocity } = e;

    wheel.value += velocity;

    if (wheel.value < -halfItemsDistanceScroll.value) {
      wheel.value = scrollLength.value - halfItemsDistanceScroll.value / 2;
    }
    if (wheel.value > scrollLength.value - halfItemsDistanceScroll.value / 2) {
      wheel.value = -halfItemsDistanceScroll.value / 2;
    }
  });
};

let context: TresContext;
let cleanupCameraFollow: (() => void) | null = null;
let gsapCtx: gsap.Context;

const canvasContainerRef = ref();
const canvasRef = ref();
const perspectiveCameraRef = ref();
const slidesGroupRef = ref();
const projectsItemRef = ref();

/**
 * ===================================================================================
 * Settings
 * itemsDistance - Tree.js units
 * ===================================================================================
 */
const itemScale = ref(2.5);
const itemsDistanceMesh = ref(4);
const itemsDistanceBetweenMesh = ref(itemScale.value * 0.6);
const itemsDistanceScroll = ref(1000);

const halfItemsDistanceScroll = computed(() => itemsDistanceScroll.value / 2);

const wheel = ref<number>(-halfItemsDistanceScroll.value / 2);

provide('wheel', wheel);

const projectPreviewItems: ProjectPreview[] = projects.reduce(
  (acc: ProjectPreview[], project) => {
    acc.push({
      ...project,
      type: 'sep',
      itemName: `${project.name}-sep`,
      // @ts-expect-error >>>
      texture: project.preview.textures[0][1],
    });

    const res: ProjectPreview[] = project.preview.textures.map(
      (textureItem, index) => ({
        ...project,
        name: project.name,
        itemName: `${project.name}-${index}`,
        type: textureItem[0],
        texture: textureItem[1],
      }),
    );

    acc.push(...res);

    return acc;
  },
  [],
);

const { onLoop } = useRenderLoop();

onLoop(() => {
  if (slidesGroupRef.value) {
    slidesGroupRef.value.position.setZ(pos.value);
  }
});

const pos = computed(
  () => wheel.value / (itemsDistanceScroll.value / itemsDistanceMesh.value),
);

const itemsCount = computed(() => projectPreviewItems.length);

const scrollLength = computed(
  () => itemsCount.value * itemsDistanceScroll.value,
);

const itemIndex = computed(() =>
  Math.floor(Math.abs(wheel.value) / itemsDistanceScroll.value),
);

const curProject = ref(projectPreviewItems[0]);

const curProjectName = computed(
  () => projectPreviewItems[itemIndex.value]?.name || '',
);

watch(curProjectName, (cur, prev) => {
  if (cur !== prev) {
    curProject.value = projectPreviewItems[itemIndex.value];
  }
});

const curProjectTitle = computed(() => curProject.value?.title);

const curProjectColor = computed(() => curProject.value?.preview.color);

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const textBackdropStyle = computed(() => {
  const color = curProjectColor.value || '#000000';
  return {
    '--backdrop-core': hexToRgba(color, 0.72),
    '--backdrop-mid': hexToRgba(color, 0.35),
    '--backdrop-edge': hexToRgba(color, 0),
  };
});

const lenisSrollEvent = ref<Lenis>();

const projectTitleRef = ref();

const titleAnimation = async () => {
  await nextTick();

  const element = '.project-title';

  // Kill previous tweens to avoid stacking
  gsap.killTweensOf(element);

  const tl = gsap.timeline();

  const duration = 0.4;
  const time = duration / 2;

  const scrollDirection = lenisSrollEvent.value
    ? lenisSrollEvent.value.direction
    : 1;

  if (scrollDirection === 1) {
    gsap.set(element, { autoAlpha: 0, scale: 0.9, z: 0.01 });
    tl.to(
      element,
      { duration: duration * 2, scale: 1.2, ease: 'slow(0.25, 0.9)' },
      time / 3,
    );
    tl.to(
      element,
      { duration, autoAlpha: 1, ease: 'slow(0.25, 0.9, true)' },
      time,
    );
  } else {
    gsap.set(element, { autoAlpha: 0, scale: 1.5, z: -0.01 });

    tl.to(
      element,
      { duration: duration * 2, scale: 1.2, ease: 'slow(0.25, 0.9)' },
      time / 2,
    );
    tl.to(
      element,
      { duration, autoAlpha: 1, ease: 'slow(0.25, 0.9, true)' },
      time,
    );
  }
};

watch(curProjectName, async (cur, prev) => {
  if (cur !== prev) {
    titleAnimation();
  }
});

// WebGL context guard — защита от чёрного экрана при потере контекста
// Вызываем в setup-фазе (не внутри onMounted!), т.к. composable регистрирует lifecycle-хуки
const webGLGuard = useWebGLContextGuard(
  () => context?.renderer.value?.domElement ?? null,
  () => { console.warn('[TreProjects] WebGL context lost'); },
  () => { console.log('[TreProjects] WebGL context restored'); },
);

onMounted(async () => {
  await nextTick();

  context = canvasRef.value.context;

  gsapCtx = gsap.context(() => {});

  titleAnimation();

  initLenis2(canvasContainerRef.value);

  cleanupCameraFollow = useTreCameraFollow(context);
});

onUnmounted(() => {
  cleanupCameraFollow?.();
  cleanupCameraFollow = null;

  gsapCtx?.revert();

  if (lenisRafId) cancelAnimationFrame(lenisRafId);
  lenisInstance?.destroy();
  lenisInstance = null;
});

// watch(canvasRef, (value) => {
//   if (value) {
// context.scene.value.background = new Color(curProjectColor.value);
// usePostEffects0(context);
// gsap.to(wheel, {
//   duration: 2,
//   value: halfItemsDistanceScroll.value,
//   ease: 'power2.out',
// });
//   }
// });

// const devicePixelRatio = window.devicePixelRatio;

// const htmlCenterRef = ref();

// let htmlCenterEl;

// watch(htmlCenterRef, async (val) => {
//   await nextTick();

//   const collection = window.document.getElementsByClassName(
//     'projects-html-center',
//   );

//   htmlCenterEl = collection[0];

//   console.log('>>>');
// });
</script>

<template lang="pug">
.projects-canvas(ref="canvasContainerRef").relative.w-full.h-full.isolate
  TresCanvas(
    ref="canvasRef"
    v-bind="getTresCanvasSettings(tier)"
    :clear-color="curProjectColor || '#000000'"
    aria-hidden="true")
    Levioso(
      :speed="0.8"
      :rotation-factor="0.8"
      :float-factor="0.2")
      TresPerspectiveCamera(
        ref="perspectiveCameraRef"
        :position="new Vector3(0, 0, itemsDistanceMesh)"
        :fov="45"
        :near="0.1"
        :look-at="[0, 0, 0]"
        :far="itemsDistanceMesh * 20")
    //- OrbitControls
    TresFog(:args="['white', 0, 20]")
    //- TresFogExp2(:args="[0x000000, 1000]")
    // Center
    TresGroup
      Html(
        center
        )
        .projects-center.flex.flex-center.w-100vw
          .text-center.projects-center-text(:style="textBackdropStyle")
            h3.projects-center__title Наши проекты
            //- h1.project-title Name Project Fix
            h1.project-title(
              ref="projectTitleRef"
              :class="`project-${curProjectName}`"
              text="size-72px white").mb-48px.font-400.lg-text-size-86px
              | {{ curProjectTitle }}
            h2.project-tags.q-gutter-sm {{ curProject?.tags.join(', ') }}
        //- .debug.text-red.flex.flex-center.w-full
          pre {{ context?.camera.value?.rotation.x }}
          //- pre {{ currentProjectItems }}
          h2 {{ itemIndex+1 }}/{{ itemsCount }} | {{ pos.toFixed(2) }} | {{ wheel.toFixed(2) }}
        .projects-center__action.flex.flex-center.w-full(v-if="curProject")
          .click-place(@click="router.push(`/project/${curProject.name}`)").pa-8
            TreBtn(
              label="Смотреть проект")
    // BG
    TreProjectsPoints(:particleCount="particleCount" :dprCap="dprCap")
    // Carousel
    TresGroup(ref="slidesGroupRef")
      //- template(
        v-for="(item, i) in projectPreviewItems"
        :key="item.itemName")
        Levioso(
          :speed="0.4"
          :rotation-factor="0.1"
          :float-factor="0.1")
      TresGroup(
        v-for="(item, i) in projectPreviewItems"
        :key="item.itemName"
        :position="new Vector3(itemsDistanceBetweenMesh * ((i+1)%2 ? -1 : 1), 0, -(itemsDistanceMesh * i))")
        //- Levioso(
          :speed="gsap.utils.random(0.4, 0.6)"
          :rotation-factor="gsap.utils.random(0.8, 1.2)"
          :float-factor="gsap.utils.random(0.8, 2.2)")
        Suspense
          template(#fallback)
            TresGroup
          TreProjectsItem(
              ref="projectsItemRef"
              :scale="itemScale"
              :item
              :item-index="i"
              :wheel
              :cur-project-name
              :items-distance-scroll
              :rotation-y="(i+1)%2 ? 0.2 : -0.2")
</template>

<style lang="sass">

// < Procects center

.projects-center-text
  position: relative
  isolation: isolate
  padding: 24px 32px
  border-radius: 24px

  &::before
    content: ''
    position: absolute
    inset: -48px
    z-index: -1
    border-radius: 36px
    background: radial-gradient(ellipse at center, var(--backdrop-core) 0%, var(--backdrop-mid) 40%, var(--backdrop-edge) 65%)
    pointer-events: none

  @media (min-width: 768px)
    padding: 36px 56px

.projects-center__title
  font-family: Alegreya
  font-size: 40px
  font-style: italic
  font-weight: 400
  line-height: 40px
  color: #FFFFFF
  margin: 0 0 20px !important
  padding: 0
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9)

.project-title
  margin: 0 0 20px !important
  padding: 0
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.95)

.project-tags
  font-family: Inter
  font-size: 32px
  font-weight: 500
  line-height: 38.73px
  color: #FFFFFF
  margin: 0 !important
  padding: 0
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9)
// >
</style>
